import { useState } from 'react'
import { useForm } from 'react-final-form'
import RouterPromptStay from 'app/router/RouterPromptStay'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import isEmpty from 'lodash/isEmpty'

import Button from 'common/components/button'
import {
  INTERCOM_DEFAULT_VERTICAL_PADDING,
  useMoveIntercomButton,
} from 'common/components/intercom/useMoveIntercomButton'
import { Topic } from 'common/components/topics/interfaces'
import Topics from 'common/components/topics/Topics'
import { useAppDispatch } from 'common/hooks/redux'
import { ArrowRight as ArrowRightIcon } from 'common/icons'
import { resetUnsavedModal } from 'features/unifiedFlow/store/unifiedFlowSlice'

import { PageState } from './BriefPage'
import MergeTags from './mergeTags'

type Props = {
  topics: Topic[]
  pageState: PageState
  isGeneratingVariants: boolean
  isReadOnly: boolean
  isGenerateStepActive?: boolean
  isAnalyzingBrief?: boolean
  isGeneratingBrief?: boolean
  contentId?: string
  setSelectedTopics: (topics: Topic[]) => void
  revertTopics: () => void
}

const GeneratedTopics = ({
  topics,
  pageState,
  isGeneratingVariants,
  isGenerateStepActive = false,
  isAnalyzingBrief = false,
  isGeneratingBrief = false,
  setSelectedTopics,
  revertTopics,
  isReadOnly,
  contentId,
}: Props) => {
  const flags = useFlags()
  const form = useForm()
  const dispatch = useAppDispatch()
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false)

  const intercomVerticalPadding =
    pageState === 'generateVariants' ? 62 : INTERCOM_DEFAULT_VERTICAL_PADDING
  useMoveIntercomButton(intercomVerticalPadding)

  const areTopicsValid = topics.every((topic) => {
    return !isEmpty(topic.name)
  })

  const onRegenerateVariants = () => {
    setIsRegenerating(true)
    dispatch(resetUnsavedModal())
    form.change('submitAction', 'generateVariants')
  }

  return (
    <>
      {(pageState === 'showGeneratedContent' ||
        pageState === 'generateVariants' ||
        pageState === 'generateBrief' ||
        pageState === 'formChangedWithTopics' ||
        pageState === 'brandVoiceChanged' ||
        pageState === 'mergeTagsChanged' ||
        pageState === 'topicsChanged') &&
        !isAnalyzingBrief && (
          <>
            <div className="relative mb-6 mt-12">
              <Topics
                isDisabled={isReadOnly}
                topics={topics}
                contentId={contentId}
                hideNewTopicsAddButton={true}
                disableReplacerConfiguration={true}
                onChange={(newTopics) => setSelectedTopics(newTopics)}
              />
            </div>
            {flags.showMergeTags && <MergeTags />}
            {(pageState === 'generateVariants' ||
              pageState === 'topicsChanged' ||
              pageState === 'showGeneratedContent' ||
              pageState === 'mergeTagsChanged') && (
              <div
                className={cx('flex justify-end items-center cta-button', {
                  hidden: pageState === 'showGeneratedContent',
                  'mb-6':
                    pageState === 'generateVariants' && !isGenerateStepActive,
                })}
              >
                {pageState === 'generateVariants' && (
                  <Button
                    disabled={
                      isReadOnly || isAnalyzingBrief || isGeneratingBrief
                    }
                    type="submit"
                    data-cy="content-generate-variants-button"
                    data-testid="content-generate-variants-button"
                    variant="primary"
                    suffixIcon={
                      <ArrowRightIcon isDefaultColor={false} size={4} />
                    }
                    loading={isAnalyzingBrief || isGeneratingBrief}
                    onClick={() => {
                      form.change('submitAction', 'generateVariants')
                    }}
                  >
                    Generate
                  </Button>
                )}
                {(pageState === 'topicsChanged' ||
                  pageState === 'mergeTagsChanged') && (
                  <>
                    <Button
                      variant="link"
                      className="mr-4"
                      data-cy="content-topics-revert-button"
                      data-testid="content-topics-revert-button"
                      disabled={isGeneratingVariants || isReadOnly}
                      onClick={() => {
                        revertTopics()
                        form?.reset()
                      }}
                    >
                      Revert
                    </Button>
                    <Button
                      type="submit"
                      data-cy="content-regenerate-button"
                      data-testid="content-regenerate-button"
                      variant="primary"
                      disabled={
                        !areTopicsValid || isGeneratingVariants || isReadOnly
                      }
                      suffixIcon={
                        <ArrowRightIcon isDefaultColor={false} size={4} />
                      }
                      loading={isGeneratingVariants}
                      onClick={onRegenerateVariants}
                    >
                      Regenerate copy
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      <RouterPromptStay
        shouldShow={
          pageState === 'topicsChanged' && !isRegenerating && !isReadOnly
        }
        onOK={() => Promise.resolve(true)}
      />
    </>
  )
}

export default GeneratedTopics
